import { useMemo } from 'react';
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts';
import { LabelValuePair } from '~/hooks/api/insights/useGetIGInsights';

import { ChartContainer } from '../chart';

export interface AgeChartTheme {
  primary?: string;
  background?: string;
  backgroundAlpha?: string;
  textBase?: string;
}

const DEFAULT_COLORS = {
  primary: '#155FEF',
  background: '#ffffff',
  backgroundAlpha: '#dee2e8',
  textBase: '#15191e',
};

const CHART_HEIGHT = 212;

interface AgeChartProps {
  ages?: LabelValuePair[] | null;
  theme?: AgeChartTheme;
}

export const AgeChart = ({ ages, theme }: AgeChartProps) => {
  const colors = {
    primary: theme?.primary ?? DEFAULT_COLORS.primary,
    background: theme?.background ?? DEFAULT_COLORS.background,
    backgroundAlpha: theme?.backgroundAlpha ?? DEFAULT_COLORS.backgroundAlpha,
    textBase: theme?.textBase ?? DEFAULT_COLORS.textBase,
  };

  const ageData = useMemo(() => {
    const ageValuesRounded =
      ages
        ?.map(({ label, value }) => ({
          label,
          value: Math.round(value * 100),
        }))
        .filter(({ value }) => value > 0) ?? [];

    const labels = ageValuesRounded.map(({ label }) => label);
    const values = ageValuesRounded.map(({ value }) => value);

    return {
      labels,
      values,
      datasets: [
        {
          label: 'Age',
          data: values,
          backgroundColor: colors.primary,
          borderColor: colors.background,
          borderWidth: 2,
        },
      ],
    };
  }, [ages, colors.primary, colors.background]);

  const chartData = useMemo(() => {
    return ageData.labels.map((label: string, index: number) => ({
      ageRange: label,
      percentage: ageData.datasets[0].data[index],
    }));
  }, [ageData]);

  // Calculate bar size based on number of items - larger bars when fewer items
  const barSize = useMemo(() => {
    const itemCount = chartData.length;
    if (itemCount <= 0) return 15;
    // Scale available height inversely with item count for better visual balance
    const baseHeight = 180;
    const heightBonus = itemCount <= 3 ? 70 : itemCount <= 4 ? 30 : 0;
    const availableHeight = baseHeight + heightBonus;
    const maxBarSize = 55;
    const minBarSize = 15;
    const calculatedSize = Math.floor((availableHeight * 0.7) / itemCount);
    return Math.max(minBarSize, Math.min(maxBarSize, calculatedSize));
  }, [chartData.length]);

  return (
    <div
      className="flex h-[280px] min-w-[280px] flex-[1_1_320px] flex-col gap-4 rounded-12 border p-4"
      style={{
        backgroundColor: colors.background,
        borderColor: colors.backgroundAlpha,
      }}
    >
      <p className="text-16 font-500" style={{ color: colors.textBase }}>
        Age distribution
      </p>
      <ChartContainer
        config={{
          percentage: {
            label: 'Percentage',
            color: colors.primary,
          },
        }}
        className="h-[212px] w-full outline-none [&_*]:outline-none"
      >
        <BarChart
          accessibilityLayer
          data={chartData}
          layout="vertical"
          width={318}
          height={CHART_HEIGHT}
          margin={{
            right: 40,
          }}
        >
          <XAxis type="number" dataKey="percentage" hide />
          <YAxis
            dataKey="ageRange"
            type="category"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            width={80}
            tick={{ fill: colors.textBase }}
          />
          <Bar
            dataKey="percentage"
            fill={colors.primary}
            radius={5}
            barSize={barSize}
          >
            <LabelList
              dataKey="percentage"
              position="right"
              offset={8}
              className="text-12 font-400"
              fill={colors.textBase}
              formatter={(value: number) => `${value}%`}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};
